import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useAuthPrompt } from '../App';
import { HelpTooltip, HelpContent } from '../components/HelpTooltip';
import LinkedAccounts from '../components/LinkedAccounts';
import { 
  getContextDashboardStats, 
  getRecentPending, 
  getUpcomingDeadlines,
  getExpenseTrackerSummary,
  getExpenseChartData 
} from '../lib/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PageSEO, pageSEO } from '../components/PageSEO';
import {
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Clock,
  ArrowRight,
  Plus,
  FileText,
  ChevronRight,
  Wallet,
  Shield,
  Calendar,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Plane,
  Monitor,
  Briefcase,
  Coffee,
  ShoppingCart,
  PiggyBank,
  Receipt,
  Car,
  DollarSign,
  BarChart3,
  Upload,
  Loader2,
  Landmark,
  Sparkles,
  Target,
  Lightbulb,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
}

// Tax Form Status Component
function TaxFormStatus({ name, status, icon: Icon, helpText }) {
  const isReady = status === 'Ready';
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-xl border transition-all
      ${isReady 
        ? 'bg-gradient-to-r from-emerald-500/5 to-transparent border-emerald-500/20' 
        : 'bg-card/30 border-border'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isReady ? 'bg-emerald-500/15' : 'bg-secondary'}`}>
          <Icon className={`w-4 h-4 ${isReady ? 'text-emerald-500' : 'text-muted-foreground'}`} strokeWidth={1.5} />
        </div>
        <span className="font-medium text-sm">{name}</span>
        {helpText && <HelpTooltip content={helpText} iconClassName="w-3.5 h-3.5" />}
      </div>
      {isReady ? (
        <div className="flex items-center gap-2 text-emerald-500">
          <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-xs font-medium">Ready</span>
        </div>
      ) : (
        <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-xs">
          Pending
        </Badge>
      )}
    </div>
  );
}

// Category Progress Component
function CategoryProgress({ icon: Icon, name, amount, total, color }) {
  const percentage = total > 0 ? (amount / total) * 100 : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <span className="text-sm font-medium">{name}</span>
        </div>
        <span className="text-sm font-semibold tnum">{formatCurrency(amount)}</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-700"
          style={{ 
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`
          }}
        />
      </div>
    </div>
  );
}

// Mini Chart Bars Component
function MiniChartBars({ labels, income, expenses }) {
  const maxVal = Math.max(...income, ...expenses, 1);
  
  return (
    <div className="flex items-end justify-between gap-1 h-[120px]">
      {labels.map((label, idx) => (
        <div key={label} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full flex gap-0.5 items-end justify-center h-[100px]">
            <div 
              className="w-2.5 bg-emerald-500 rounded-t transition-all"
              style={{ height: `${(income[idx] / maxVal) * 100}%`, minHeight: '4px' }}
            />
            <div 
              className="w-2.5 bg-red-500 rounded-t transition-all"
              style={{ height: `${(expenses[idx] / maxVal) * 100}%`, minHeight: '4px' }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{label.slice(-2)}</span>
        </div>
      ))}
    </div>
  );
}

// Transaction Item Component
function TransactionItem({ txn }) {
  const isIncome = txn.amount > 0 || txn.entry_type === 'income';
  
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isIncome ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
          {isIncome ? (
            <ArrowUpRight className="w-4 h-4 text-emerald-500" />
          ) : (
            <ArrowDownRight className="w-4 h-4 text-red-500" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium truncate max-w-[160px]">
            {txn.description || txn.vendor || txn.merchant_name || 'Transaction'}
          </p>
          <p className="text-xs text-muted-foreground">{txn.date}</p>
        </div>
      </div>
      <span className={`text-sm font-semibold ${isIncome ? 'text-emerald-500' : 'text-red-500'}`}>
        {isIncome ? '+' : ''}{formatCurrency(Math.abs(txn.amount))}
      </span>
    </div>
  );
}

export default function UnifiedDashboard({ context }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const viewMode = context || (location.pathname.startsWith('/personal') ? 'personal' : 'business');
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState({
    totalExpenses: 0,
    totalWriteoffs: 0,
    pendingCount: 0,
    mileageTotal: 0,
    receiptsMatched: 0,
    receiptsTotal: 0
  });
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expenses: 0,
    net_profit: 0,
    profit_margin: 0,
    expenses_by_category: {},
    income_by_category: {},
    transaction_count: 0,
    recent_transactions: []
  });
  const [chartData, setChartData] = useState({ labels: [], income: [], expenses: [] });
  const [deadlines, setDeadlines] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [insights, setInsights] = useState({ insights: [], change_percentage: 0 });
  const [budgetProgress, setBudgetProgress] = useState([]);
  const [goalsProgress, setGoalsProgress] = useState([]);
  
  const requireAuthNav = (path, feature) => {
    if (!isAuthenticated) {
      showAuthPrompt(feature);
      return;
    }
    navigate(path);
  };

  async function fetchData() {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, summaryRes, chartRes, pendingRes, deadlinesRes] = await Promise.all([
        getContextDashboardStats(viewMode),
        getExpenseTrackerSummary(period),
        getExpenseChartData('6months'),
        getRecentPending(),
        getUpcomingDeadlines()
      ]);
      
      setStats(statsRes.data || {});
      setSummary(summaryRes.data || {});
      setChartData(chartRes.data || { labels: [], income: [], expenses: [] });
      setPendingTransactions(pendingRes.data || []);
      setDeadlines(deadlinesRes.data || []);
      
      // Fetch personal-specific data
      if (viewMode === 'personal') {
        try {
          const [insightsRes, budgetsRes, goalsRes] = await Promise.all([
            axios.get(`${API_URL}/api/personal/spending-insights`, { headers }),
            axios.get(`${API_URL}/api/personal/budgets`, { headers }),
            axios.get(`${API_URL}/api/personal/goals`, { headers })
          ]);
          setInsights(insightsRes.data || { insights: [] });
          setBudgetProgress(budgetsRes.data.budgets || []);
          setGoalsProgress(goalsRes.data.goals || []);
        } catch (e) {
          // Demo data for non-authenticated users
          setInsights({
            insights: [
              { type: 'info', message: 'Your biggest spending category is Food & Dining at $425' },
              { type: 'success', message: 'Great job! You\'ve spent 12% less than last month' }
            ],
            change_percentage: -12
          });
          setBudgetProgress([
            { id: '1', category: 'Food & Dining', limit: 600 },
            { id: '2', category: 'Shopping', limit: 400 }
          ]);
          setGoalsProgress([
            { id: '1', name: 'Vacation Fund', target: 5000, saved: 2750 },
            { id: '2', name: 'Emergency Fund', target: 10000, saved: 6500 }
          ]);
        }
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [viewMode, period]);

  // Business expense categories with icons and colors
  const businessCategories = [
    { icon: Monitor, name: 'Software & Services', color: 'hsl(210 50% 55%)' },
    { icon: Plane, name: 'Travel', color: 'hsl(200 80% 55%)' },
    { icon: Briefcase, name: 'Office Supplies', color: 'hsl(160 60% 50%)' },
    { icon: Coffee, name: 'Meals & Entertainment', color: 'hsl(38 90% 55%)' },
    { icon: ShoppingCart, name: 'Marketing', color: 'hsl(280 60% 55%)' }
  ];

  const periodLabel = period === 'month' ? 'This Month' : period === 'quarter' ? 'This Quarter' : 'This Year';
  const totalIncome = summary?.total_income || 0;
  const totalExpenses = summary?.total_expenses || stats?.totalExpenses || 0;
  const netProfit = summary?.net_profit || (totalIncome - totalExpenses);
  const profitMargin = summary?.profit_margin || (totalIncome > 0 ? Math.round((netProfit / totalIncome) * 100) : 0);
  const txnCount = summary?.transaction_count || 0;
  const expenseCats = summary?.expenses_by_category || {};
  const recentTxns = summary?.recent_transactions || [];
  const chartLabels = chartData?.labels || [];
  const chartIncome = chartData?.income || [];
  const chartExpenses = chartData?.expenses || [];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto" data-testid="unified-dashboard">
      <PageSEO {...pageSEO.dashboard} />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-['Space_Grotesk']">
            {viewMode === 'personal' ? 'Personal ' : 'Business '}
            <span className="text-[hsl(var(--accent))]">Dashboard</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {viewMode === 'personal' ? 'Your personal financial overview' : 'Your business financial command center'}
          </p>
        </div>
        
        {/* Period Toggle */}
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-1">
            {['month', 'quarter', 'year'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all capitalize ${
                  period === p ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`period-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Income */}
        <Card className="card-premium" data-testid="kpi-income">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-xs sm:text-sm text-muted-foreground">Total Income</p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-500">{formatCurrency(totalIncome)}</p>
                <p className="text-xs text-muted-foreground">{periodLabel}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-xl bg-emerald-500/10">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Expenses */}
        <Card className="card-premium" data-testid="kpi-expenses">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-xs sm:text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-xl sm:text-2xl font-bold text-red-500">{formatCurrency(totalExpenses)}</p>
                <p className="text-xs text-muted-foreground">{periodLabel}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-xl bg-red-500/10">
                <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card className="card-premium" data-testid="kpi-profit">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-xs sm:text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-xl sm:text-2xl font-bold ${netProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {formatCurrency(netProfit)}
                </p>
                <p className="text-xs text-muted-foreground">{profitMargin}% margin</p>
              </div>
              <div className={`p-2 sm:p-3 rounded-xl ${netProfit >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                <PiggyBank className={`w-5 h-5 sm:w-6 sm:h-6 ${netProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions / Mileage */}
        {viewMode === 'business' ? (
          <Card 
            className="card-premium cursor-pointer hover:border-[hsl(var(--accent))]/50 transition-all" 
            onClick={() => requireAuthNav(`/${viewMode}/mileage`, 'Track Mileage')}
            data-testid="kpi-mileage"
          >
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-xs sm:text-sm text-muted-foreground">Mileage Deduction</p>
                  <p className="text-xl sm:text-2xl font-bold text-[hsl(var(--accent))]">
                    {formatCurrency((stats?.mileageTotal || 0) * 0.70)}
                  </p>
                  <p className="text-xs text-muted-foreground">{stats?.mileageTotal || 0} miles logged</p>
                </div>
                <div className="p-2 sm:p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                  <Car className="w-5 h-5 sm:w-6 sm:h-6 text-[hsl(var(--accent))]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card 
            className="card-premium cursor-pointer hover:border-[hsl(var(--accent))]/50 transition-all" 
            onClick={() => requireAuthNav(`/${viewMode}/expenses/manage`, 'View Transactions')}
            data-testid="kpi-transactions"
          >
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-xs sm:text-sm text-muted-foreground">Transactions</p>
                  <p className="text-xl sm:text-2xl font-bold text-[hsl(var(--accent))]">{txnCount}</p>
                  <p className="text-xs text-muted-foreground">{periodLabel}</p>
                </div>
                <div className="p-2 sm:p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                  <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-[hsl(var(--accent))]" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Chart + Categories */}
        <div className="lg:col-span-2 space-y-6">
          {/* Income vs Expenses Chart */}
          <Card className="card-premium">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Income vs Expenses</CardTitle>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-emerald-500 rounded" />
                    <span className="text-muted-foreground">Income</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded" />
                    <span className="text-muted-foreground">Expenses</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {chartLabels.length > 0 ? (
                <MiniChartBars labels={chartLabels} income={chartIncome} expenses={chartExpenses} />
              ) : (
                <div className="h-[120px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No chart data yet</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expense Breakdown */}
          <Card className="card-premium">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Expense Breakdown</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-[hsl(var(--accent))] text-xs"
                  onClick={() => navigate(`/${viewMode}/expenses/manage`)}
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(expenseCats).length > 0 ? (
                Object.entries(expenseCats)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([name, amount], idx) => {
                    const cat = businessCategories[idx % businessCategories.length];
                    return (
                      <CategoryProgress
                        key={name}
                        icon={cat.icon}
                        name={name}
                        amount={amount}
                        total={totalExpenses}
                        color={cat.color}
                      />
                    );
                  })
              ) : (
                <div className="py-6 text-center text-muted-foreground">
                  <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No expense data yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked Bank Accounts - Business Only */}
          {viewMode === 'business' && (
            <LinkedAccounts showTitle={true} compact={true} />
          )}
        </div>

        {/* Right Column - Activity, Actions, Forms */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card className="card-premium">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-auto py-3 flex-col gap-2"
                onClick={() => requireAuthNav(`/${viewMode}/docs?tab=scan`, 'Scan Receipt')}
                data-testid="quick-scan"
              >
                <Receipt className="w-5 h-5 text-[hsl(var(--accent))]" />
                <span className="text-xs">Scan Receipt</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-3 flex-col gap-2"
                onClick={() => requireAuthNav(`/${viewMode}/expenses/manage?tab=manual`, 'Add Entry')}
                data-testid="quick-add"
              >
                <Plus className="w-5 h-5 text-emerald-500" />
                <span className="text-xs">Add Entry</span>
              </Button>
              {viewMode === 'business' ? (
                <Button
                  variant="outline"
                  className="h-auto py-3 flex-col gap-2"
                  onClick={() => requireAuthNav(`/${viewMode}/mileage`, 'Log Mileage')}
                  data-testid="quick-mileage"
                >
                  <Car className="w-5 h-5 text-blue-500" />
                  <span className="text-xs">Log Mileage</span>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="h-auto py-3 flex-col gap-2"
                  onClick={() => requireAuthNav('/personal/budgets', 'Set Budget')}
                  data-testid="quick-budget"
                >
                  <Wallet className="w-5 h-5 text-blue-500" />
                  <span className="text-xs">Budgets</span>
                </Button>
              )}
              {viewMode === 'business' ? (
                <Button
                  variant="outline"
                  className="h-auto py-3 flex-col gap-2"
                  onClick={() => requireAuthNav(`/${viewMode}/reports`, 'Generate Report')}
                  data-testid="quick-report"
                >
                  <FileText className="w-5 h-5 text-purple-500" />
                  <span className="text-xs">Reports</span>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="h-auto py-3 flex-col gap-2"
                  onClick={() => requireAuthNav('/personal/goals', 'View Goals')}
                  data-testid="quick-goals"
                >
                  <Target className="w-5 h-5 text-purple-500" />
                  <span className="text-xs">Goals</span>
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Spending Insights - Personal Only */}
          {viewMode === 'personal' && insights.insights?.length > 0 && (
            <Card className="card-premium">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                  <CardTitle className="text-lg">Spending Insights</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {insights.insights.slice(0, 3).map((insight, i) => (
                  <div 
                    key={i}
                    className={`flex items-start gap-3 p-3 rounded-xl border ${
                      insight.type === 'success' ? 'bg-emerald-500/5 border-emerald-500/20' :
                      insight.type === 'warning' ? 'bg-amber-500/5 border-amber-500/20' :
                      'bg-blue-500/5 border-blue-500/20'
                    }`}
                  >
                    <Info className={`w-4 h-4 mt-0.5 ${
                      insight.type === 'success' ? 'text-emerald-500' :
                      insight.type === 'warning' ? 'text-amber-500' :
                      'text-blue-500'
                    }`} />
                    <p className="text-sm">{insight.message}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Budget Progress - Personal Only */}
          {viewMode === 'personal' && budgetProgress.length > 0 && (
            <Card className="card-premium">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-[hsl(var(--accent))]" />
                    <CardTitle className="text-lg">Budget Status</CardTitle>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-[hsl(var(--accent))] text-xs"
                    onClick={() => navigate('/personal/budgets')}
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {budgetProgress.slice(0, 3).map((budget) => {
                  const spent = expenseCats[budget.category] || 0;
                  const pct = budget.limit > 0 ? Math.min((spent / budget.limit) * 100, 100) : 0;
                  const isOver = spent > budget.limit;
                  return (
                    <div key={budget.id} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{budget.category}</span>
                        <span className={isOver ? 'text-red-500' : 'text-muted-foreground'}>
                          {formatCurrency(spent)} / {formatCurrency(budget.limit)}
                        </span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Savings Goals Progress - Personal Only */}
          {viewMode === 'personal' && goalsProgress.length > 0 && (
            <Card className="card-premium">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-500" />
                    <CardTitle className="text-lg">Savings Goals</CardTitle>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-[hsl(var(--accent))] text-xs"
                    onClick={() => navigate('/personal/goals')}
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {goalsProgress.slice(0, 3).map((goal) => {
                  const pct = goal.target > 0 ? Math.min((goal.saved / goal.target) * 100, 100) : 0;
                  const isComplete = goal.saved >= goal.target;
                  return (
                    <div key={goal.id} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{goal.name}</span>
                        <span className={isComplete ? 'text-emerald-500' : 'text-muted-foreground'}>
                          {formatCurrency(goal.saved)} / {formatCurrency(goal.target)}
                        </span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${isComplete ? 'bg-emerald-500' : 'bg-purple-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Recent Activity */}
          <Card className="card-premium">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <Badge variant="outline" className="text-xs">{recentTxns.length} new</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {recentTxns.length > 0 ? (
                <div>
                  {recentTxns.slice(0, 4).map((txn, idx) => (
                    <TransactionItem key={txn.id || idx} txn={txn} />
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center text-muted-foreground">
                  <Receipt className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tax Forms Status - Business Only */}
          {viewMode === 'business' && (
            <Card className="card-premium">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                    <CardTitle className="text-lg">Tax Forms</CardTitle>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-[hsl(var(--accent))] text-xs"
                    onClick={() => navigate(`/${viewMode}/reports`)}
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <TaxFormStatus 
                  name="Schedule C" 
                  status="Ready" 
                  icon={FileText}
                  helpText={HelpContent.dashboard.scheduleC}
                />
                <TaxFormStatus 
                  name="Mileage Log" 
                  status={stats?.mileageTotal > 0 ? 'Ready' : 'Pending'} 
                  icon={Car}
                  helpText={HelpContent.dashboard.mileageLog}
                />
                <TaxFormStatus 
                  name="1099 Forms" 
                  status="Pending" 
                  icon={FileText}
                  helpText={HelpContent.dashboard.form1099}
                />
              </CardContent>
            </Card>
          )}

          {/* Upcoming Deadlines - Business Only */}
          {viewMode === 'business' && deadlines.length > 0 && (
            <Card className="card-premium">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                  <CardTitle className="text-lg">Upcoming Deadlines</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {deadlines.slice(0, 3).map((deadline, i) => {
                  const isUrgent = deadline.days_until <= 7;
                  return (
                    <div 
                      key={i}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                        isUrgent 
                          ? 'bg-red-500/5 border-red-500/30' 
                          : 'bg-secondary/30 border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isUrgent && <AlertTriangle className="w-4 h-4 text-red-500" />}
                        <div>
                          <p className="font-medium text-sm">{deadline.name}</p>
                          <p className="text-xs text-muted-foreground">{deadline.date}</p>
                        </div>
                      </div>
                      <Badge variant={isUrgent ? 'destructive' : 'outline'} className="text-xs">
                        {deadline.days_until}d
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Pending Review - shown for both */}
          {pendingTransactions.length > 0 && (
            <Card className="card-premium">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                    <CardTitle className="text-lg">Pending Review</CardTitle>
                  </div>
                  <Badge className="bg-amber-500/20 text-amber-500 border-0 text-xs">
                    {pendingTransactions.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {pendingTransactions.slice(0, 3).map((txn, i) => (
                  <div 
                    key={i}
                    className="flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-border hover:border-[hsl(var(--accent))]/50 transition-all cursor-pointer"
                    onClick={() => navigate(`/${viewMode}/transactions?category=pending`)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-amber-500/10">
                        <CreditCard className="w-4 h-4 text-amber-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm truncate max-w-[120px]">{txn.description || txn.merchant_name}</p>
                        <p className="text-xs text-muted-foreground">{txn.date}</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold">{formatCurrency(Math.abs(txn.amount))}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
